
import React, { useEffect, useState, useRef, useCallback } from 'react';

// Layouts & Styles
import { StatusPageMainContainer, Header, SubHeader, DashboardContainer, StatusCard, RobotStatusContainer, RobotStatusHeader, RobotStatusBody, StatusCardData, FixedScrollContainer, ScrollableContainer, StatusCol, EmptyStatusCol, ColumnGroup } from './styles';

// Components
import RobotStatusDashboardColumn from '../RobotStatusDashboardColumn';

// hooks
import useWebsocketConnection from 'hooks/useWebsocketConnection';

// Constants
import { PayloadActions, CallbackActions, ROBOT_STATUS_DATA_KEYS, ROBOT_STATUS_DEFAULT_VALUE, ROBOT_UNKNOWN_VALUE } from 'Config/websocketConstants';
import { RECORDING_SETTINS, WHITE_BALANCE_SETTINGS, IMAGE_SETTINGS, EXPOSURE_SETTINGS } from 'Config/zcamConstants';

// utils
import { validateRobotStatusData } from 'Utils/websocketUtils';

const RobotStatusDashboard = ({
    dashboardHeader,
    dashboardSubHeader,
    robotAirIdsString,
    robotAirIdstoNickNames,
    robotAirIds,
    wsClientName,
    robotAirIdsToProjectPlaceholders = null, // Ex: {airId: placeholder}
}) => {
    const [robotAirInfo, setRobotAirInfo] = useState({});
    const [onlineRobotAirIds, setOnlineRobotAirIds] = useState([]);
    const [onlineRobotAirIdsDisplayOrder, setOnlineRobotAirIdsDisplayOrder] = useState([]);
    const [offlineRobotAirIds, setOfflineRobotAirIds] = useState([]);
    const [localUpdateTimeById, setLocalUpdateTimeById] = useState({});
    const [utcUpdateTimeById, setUtcUpdateTimeById] = useState({}); // UTC ISO string format used to check stale data
    const utcUpdateTimeByIdRef = useRef({}) // Allow setInterval to see the latest online Robots data update time state
    const [lanIpById, setLanIpById] = useState({});
    const [vpnIpById, setVpnIpById] = useState({});
    const [recordingResolutionById, setRecordingResolutionById] = useState({});
    const [recordingFpsById, setRecordingFpsById] = useState({});
    const [recordingCodecById, setRecordingCodecById] = useState({});
    const [cardAvailTimeById, setCardAvailTimeById] = useState({});
    const [isRecordingLiveById, setIsRecordingLiveById] = useState({});
    const [streamingDestinationById, setStreamingDestinationById] = useState({});
    const [streamingBitrateById, setStreamingBitrateById] = useState({});
    const [isStreamingLiveById, setIsStreamingLiveById] = useState({});
    const [streamingFpsById, setStreamingFpsById] = useState({});
    const [streamingCodecById, setStreamingCodecById] = useState({});
    const [whiteBalanceById, setWhiteBalanceById] = useState({});
    const [tintById, setTintById] = useState({});
    const [lutById, setLutById] = useState({});
    const [isoById, setIsoById] = useState({});
    const [fStopById, setFStopById] = useState({});
    const [shutterById, setShutterById] = useState({});
    const [zcamFirmwareVerById, setZcamFirmwareVerById] = useState({});
    const [apiVerById, setApiVerById] = useState({});
    const [cpuLoadById, setCpuLoadById] = useState({});
    const [cpuTempById, setCpuTempById] = useState({});
    const [memUsageById, setMemUsageById] = useState({});
    const [diskUsageById, setDiskUsageById] = useState({});
    const [contrastById, setContrastById] = useState({});
    const [brightnessById, setBrightnessById] = useState({});
    const [saturationtById, setSaturationById] = useState({});
    const [clientCountById, setClientCountById] = useState({});

    const statusSetterMap = useRef({
        [ROBOT_STATUS_DATA_KEYS.VPN_IP]: setVpnIpById,
        [ROBOT_STATUS_DATA_KEYS.LAN_IP]: setLanIpById,
        [ROBOT_STATUS_DATA_KEYS.API_VERSION]: setApiVerById,
        [ROBOT_STATUS_DATA_KEYS.CPU_LOAD]: setCpuLoadById,
        [ROBOT_STATUS_DATA_KEYS.CPU_TEMP]: setCpuTempById,
        [ROBOT_STATUS_DATA_KEYS.MEM_USAGE]: setMemUsageById,
        [ROBOT_STATUS_DATA_KEYS.DISK_USAGE]: setDiskUsageById,
        [ROBOT_STATUS_DATA_KEYS.CLIENT_CNT]: setClientCountById,
        [ROBOT_STATUS_DATA_KEYS.STREAMING_FPS]: setStreamingFpsById,
        [ROBOT_STATUS_DATA_KEYS.STREAMING_CODEC]: setStreamingCodecById,
        [ROBOT_STATUS_DATA_KEYS.STREAMING_DES]: setStreamingDestinationById,
        [ROBOT_STATUS_DATA_KEYS.STREAMING_BITRATE]: setStreamingBitrateById,
        [ROBOT_STATUS_DATA_KEYS.STREAMING_STATUS]: setIsStreamingLiveById,
        [ROBOT_STATUS_DATA_KEYS.LUT]: setLutById,
        [ROBOT_STATUS_DATA_KEYS.CONTRAST]: setContrastById,
        [ROBOT_STATUS_DATA_KEYS.BRIGHTNESS]: setBrightnessById,
        [ROBOT_STATUS_DATA_KEYS.SATURATION]: setSaturationById,
        [ROBOT_STATUS_DATA_KEYS.SHUTTER]: setShutterById,
        [ROBOT_STATUS_DATA_KEYS.F_STOP]: setFStopById,
        [ROBOT_STATUS_DATA_KEYS.ISO]: setIsoById,
        [ROBOT_STATUS_DATA_KEYS.WHITE_BALANCE]: setWhiteBalanceById,
        [ROBOT_STATUS_DATA_KEYS.RECORDING_RESOLUTION]: setRecordingResolutionById,
        [ROBOT_STATUS_DATA_KEYS.RECORDING_FPS]: setRecordingFpsById,
        [ROBOT_STATUS_DATA_KEYS.RECORDING_CODEC]: setRecordingCodecById,
        [ROBOT_STATUS_DATA_KEYS.CARD_TIME]: setCardAvailTimeById,
        [ROBOT_STATUS_DATA_KEYS.RECORDING_STATUS]: setIsRecordingLiveById,
        [ROBOT_STATUS_DATA_KEYS.ZCAM_FIRMWARE_VER]: setZcamFirmwareVerById,
        [ROBOT_STATUS_DATA_KEYS.TINT]: setTintById,
    });

    const updateRobotStatus = useCallback((updatedStatus) => {
        try {
            let newStatusObj = updatedStatus;
            const isValidData = validateRobotStatusData(newStatusObj);
            if (!isValidData) {
                // console.log('Invalid robot status message');
                return
            }
            let robotAirId = newStatusObj[ROBOT_STATUS_DATA_KEYS.AIR_ID];
            let robotName = newStatusObj[ROBOT_STATUS_DATA_KEYS.ROBOT_NAME];

            setRobotAirInfo(prev => {
                let robotInfo = `${robotName} / ${robotAirId}`;
                if (prev[robotAirId] === robotInfo) {
                    return prev;
                }
                return {...prev, [robotAirId]: robotInfo};
            })

            setLocalUpdateTimeById(prev => ({
                ...prev,
                [robotAirId]: new Date(newStatusObj.timestamp).toLocaleString()
            }))
            setUtcUpdateTimeById(prev => ({
                ...prev,
                [robotAirId]: new Date(newStatusObj.timestamp)
            }))

            for (const [statusKey, setterFunc] of Object.entries(statusSetterMap.current)) {
                setterFunc(prev => {
                    let newStatus = newStatusObj[statusKey] === "" ? ROBOT_STATUS_DEFAULT_VALUE : newStatusObj[statusKey];
                    if (prev[robotAirId] === newStatus) {
                        return prev;
                    }
                    return {...prev, [robotAirId]: newStatus};
                });
            }
        } catch (err) {
            console.log(err)
        }
    }, []);

    const actionToCallbackObj = useRef({
        [CallbackActions.ROBOT_STATUS_UPDATE]: updateRobotStatus,
    })

    const { sendPayload } = useWebsocketConnection(wsClientName, actionToCallbackObj.current);

    const compareRobotArray = (currentRobotArray, newArray) => {
        if (currentRobotArray.length !== newArray.length) {
            return false;
        }
        for (const robotId of currentRobotArray) {
            if (!newArray.includes(robotId)) {
                return false;
            }
        }
        return true;
    }

    const sortPlaceHolder = (a, b) => {
        let aEmptyOrNotAvailable = (a === "" || a === ROBOT_STATUS_DEFAULT_VALUE);
        let bEmptyOrNotAvailable = (b === "" || b === ROBOT_STATUS_DEFAULT_VALUE);
        if (aEmptyOrNotAvailable && bEmptyOrNotAvailable) {
            return 0;
        } else if (aEmptyOrNotAvailable) {
            return 1; // a after b
        } else if (bEmptyOrNotAvailable) {
            return -1;
        } else {
            return Number(a) - Number(b);
        }
    }

    useEffect(() => {
        // Periodly clean up stale status in case not receiving new robot status
        const periodCheckIntervalSecond = 60*1000;
        const removeThresholdTime = 60*1000;

        const checkOnlineRobotIntervalId = setInterval(() => {
          const currentOnlineRobotIds = Object.keys(utcUpdateTimeByIdRef.current);
          if (currentOnlineRobotIds.length > 0) {
            const now = new Date();
            let needRemoveAirIds = []
            currentOnlineRobotIds.map((airId, idx) => {
              const timeDiff = now - utcUpdateTimeByIdRef.current[airId];
              if (timeDiff >= removeThresholdTime) {
                needRemoveAirIds.push(airId);
              }
            })
            if (needRemoveAirIds.length > 0) {
              const newUtcUpdateTimeById = {...utcUpdateTimeByIdRef.current};
              const newLocalUpdateTimeById = {...localUpdateTimeById};
              needRemoveAirIds.map((airId) => {
                delete newUtcUpdateTimeById[airId];
                delete newLocalUpdateTimeById[airId];
              })
              setUtcUpdateTimeById(newUtcUpdateTimeById);
              setLocalUpdateTimeById(newLocalUpdateTimeById);
            }
          }
        }, periodCheckIntervalSecond)

        return (() => {
            clearInterval(checkOnlineRobotIntervalId);
        })
    }, []);

    useEffect(() => {
        utcUpdateTimeByIdRef.current = utcUpdateTimeById;
    }, [utcUpdateTimeById]);

    useEffect(() => {
        if (robotAirIds.length > 0) {
            const newOnlineRobots = Object.keys(localUpdateTimeById);
            if (newOnlineRobots.length > 0) {
                if (!compareRobotArray(onlineRobotAirIds, newOnlineRobots)) {
                    // Find new Offline Robots
                    const offlineRobots = robotAirIds.filter(airId => {
                        return !newOnlineRobots.includes(airId);
                    });
                    setOnlineRobotAirIds(newOnlineRobots);
                    setOfflineRobotAirIds(offlineRobots);
                }
            } else {
                setOfflineRobotAirIds(robotAirIds);
            }
        }

        return (() => {})
    }, [robotAirIds, localUpdateTimeById]);

    useEffect(() => {
        if (onlineRobotAirIds.length > 0) {
            if (robotAirIdsToProjectPlaceholders) { // Project dashboard, sort by placeholder
                let sortedRobotAirIdsToProjectPlaceholders = Object.entries(robotAirIdsToProjectPlaceholders).toSorted(([keyA, valA], [keyB, valB]) => sortPlaceHolder(valA, valB));
                let newDisplayOrder = [];
                sortedRobotAirIdsToProjectPlaceholders.map(([airId, placeholder]) => {
                    if (onlineRobotAirIds.includes(airId)) {
                        newDisplayOrder.push(airId);
                    }
                })
                setOnlineRobotAirIdsDisplayOrder(newDisplayOrder);
            } else { // Org Dashboard, sort by nickname alphabetically
                if (Object.keys(robotAirIdstoNickNames).length > 0) {
                    let robotNamesToAirIds = {};
                    onlineRobotAirIds.map((airId) => {
                        let robotName = robotAirIdstoNickNames?.[airId] ?? ROBOT_UNKNOWN_VALUE
                        robotNamesToAirIds[robotName] = airId;
                    });

                    let sortedNames = Object.values(robotAirIdstoNickNames).toSorted((a, b) => a.localeCompare(b));
                    let newDisplayOrder = [];
                    sortedNames.map((robotName) => {
                        let airId = robotNamesToAirIds[robotName];
                        if (onlineRobotAirIds.includes(airId)) {
                            newDisplayOrder.push(airId);
                        }
                    })
                    setOnlineRobotAirIdsDisplayOrder(newDisplayOrder);
                } else {
                    setOnlineRobotAirIdsDisplayOrder(onlineRobotAirIds);
                }
            }
        }
        return (() => {})
    }, [onlineRobotAirIds]);

    useEffect(() => { // The manager opens the socket and resubscribes after reconnects
        if (robotAirIdsString.length > 0) {
            sendPayload({
                "action": PayloadActions.CONNECT_AIR_ID_GROUPS,
                "data": { "air_ids": robotAirIdsString }
            })
        }
    }, [robotAirIdsString]);

  return (
    <StatusPageMainContainer>
        <div className="d-flex justify-content-between">
            <div>
                <Header>{dashboardHeader}</Header>
                <br />
                <SubHeader>{dashboardSubHeader}</SubHeader>
            </div>
        </div>

        <DashboardContainer>
            {(robotAirIds && robotAirIds.length > 0) ?(
                <>
                    {onlineRobotAirIds && onlineRobotAirIds.length > 0 && (
                        <RobotStatusContainer>
                            <RobotStatusHeader>
                                <FixedScrollContainer>
                                    <StatusCard>
                                        <StatusCardData isTitle>Online Robots:</StatusCardData>
                                    </StatusCard>
                                </FixedScrollContainer>
                            </RobotStatusHeader>
                            <RobotStatusBody>
                                <FixedScrollContainer>
                                    <RobotStatusDashboardColumn
                                        onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                        columnTitle={'Robot NickName'}
                                        columnDataObj={robotAirIdstoNickNames}
                                    />
                                    {robotAirIdsToProjectPlaceholders && (
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Display Number'}
                                            columnDataObj={robotAirIdsToProjectPlaceholders}
                                        />
                                    )}
                                </FixedScrollContainer>
                                <ScrollableContainer>
                                    <ColumnGroup>
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Clients'}
                                            columnDataObj={clientCountById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'LAN'}
                                            columnDataObj={lanIpById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'VPN IP'}
                                            columnDataObj={vpnIpById}
                                        />
                                    </ColumnGroup>
                                    <ColumnGroup>
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={RECORDING_SETTINS.INT_RECORD.title}
                                            columnDataObj={recordingResolutionById}
                                            liveStatusObj={isRecordingLiveById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={RECORDING_SETTINS.INT_RECORD}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={RECORDING_SETTINS.INT_FPS.title}
                                            columnDataObj={recordingFpsById}
                                            liveStatusObj={isRecordingLiveById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={RECORDING_SETTINS.INT_FPS}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={RECORDING_SETTINS.INT_CODEC.title}
                                            columnDataObj={recordingCodecById}
                                            liveStatusObj={isRecordingLiveById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={RECORDING_SETTINS.INT_CODEC}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Card TRT'}
                                            columnDataObj={cardAvailTimeById}
                                        />
                                    </ColumnGroup>
                                    <ColumnGroup>
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Stream URL'}
                                            columnDataObj={streamingDestinationById}
                                            liveStatusObj={isStreamingLiveById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Stream Mbps'}
                                            columnDataObj={streamingBitrateById}
                                            liveStatusObj={isStreamingLiveById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Stream FPS'}
                                            columnDataObj={streamingFpsById}
                                            liveStatusObj={isStreamingLiveById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Stream Codec'}
                                            columnDataObj={streamingCodecById}
                                            liveStatusObj={isStreamingLiveById}
                                        />
                                    </ColumnGroup>
                                    <ColumnGroup>
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={WHITE_BALANCE_SETTINGS.WHITE_BALANCE.title}
                                            columnDataObj={whiteBalanceById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={WHITE_BALANCE_SETTINGS.WHITE_BALANCE}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={WHITE_BALANCE_SETTINGS.TINT.title}
                                            columnDataObj={tintById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={WHITE_BALANCE_SETTINGS.TINT}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={IMAGE_SETTINGS.CONTRAST.title}
                                            columnDataObj={contrastById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={IMAGE_SETTINGS.CONTRAST}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={IMAGE_SETTINGS.BRIGHTNESS.title}
                                            columnDataObj={brightnessById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={IMAGE_SETTINGS.BRIGHTNESS}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={IMAGE_SETTINGS.SATURATION.title}
                                            columnDataObj={saturationtById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={IMAGE_SETTINGS.SATURATION}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={IMAGE_SETTINGS.PROFILE.title}
                                            columnDataObj={lutById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={IMAGE_SETTINGS.PROFILE}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={EXPOSURE_SETTINGS.ISO.title}
                                            columnDataObj={isoById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={EXPOSURE_SETTINGS.ISO}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={EXPOSURE_SETTINGS.F_STOP.title}
                                            columnDataObj={fStopById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={EXPOSURE_SETTINGS.F_STOP}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={EXPOSURE_SETTINGS.SHUTTER.title}
                                            columnDataObj={shutterById}
                                            vpnIpById={vpnIpById}
                                            robotSettingsConstant={EXPOSURE_SETTINGS.SHUTTER}
                                            robotAirIdstoNickNames={robotAirIdstoNickNames}
                                        />
                                    </ColumnGroup>
                                    <ColumnGroup>
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'ZCam FW'}
                                            columnDataObj={zcamFirmwareVerById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'AIR API'}
                                            columnDataObj={apiVerById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'CPU Load'}
                                            columnDataObj={cpuLoadById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'CPU Temp'}
                                            columnDataObj={cpuTempById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'CPU Mem'}
                                            columnDataObj={memUsageById}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'CPU Disk'}
                                            columnDataObj={diskUsageById}
                                        />
                                    </ColumnGroup>
                                    <ColumnGroup>
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Robot AIR Info'}
                                            columnDataObj={robotAirInfo}
                                        />
                                        <RobotStatusDashboardColumn
                                            onlineRobotAirIdsDisplayOrder={onlineRobotAirIdsDisplayOrder}
                                            columnTitle={'Data Update Time'}
                                            columnDataObj={localUpdateTimeById}
                                        />
                                    </ColumnGroup>
                                    <EmptyStatusCol></EmptyStatusCol>
                                </ScrollableContainer>
                            </RobotStatusBody>
                        </RobotStatusContainer>
                    )}
                    {offlineRobotAirIds && offlineRobotAirIds.length > 0 && (
                        <RobotStatusContainer>
                            <RobotStatusHeader>
                                <FixedScrollContainer>
                                    <StatusCard>
                                        <StatusCardData isTitle>Robot Offline or Data Not Available:</StatusCardData>
                                    </StatusCard>
                                </FixedScrollContainer>
                            </RobotStatusHeader>
                            <RobotStatusBody>
                                <FixedScrollContainer>
                                    <StatusCol>
                                        <StatusCard>
                                            <StatusCardData isTitle>NickName</StatusCardData>
                                        </StatusCard>
                                        <StatusCard>
                                            <StatusCardData isTitle>AIR ID</StatusCardData>
                                        </StatusCard>
                                    </StatusCol>
                                </FixedScrollContainer>
                                <ScrollableContainer>
                                    {offlineRobotAirIds.map((robotAirId, idx) => {
                                        return (
                                            <StatusCol key={robotAirId}>
                                                <StatusCard>
                                                    <StatusCardData isInfo>{robotAirIdstoNickNames[robotAirId]}</StatusCardData>
                                                </StatusCard>
                                                <StatusCard>
                                                    <StatusCardData isInfo>{robotAirId}</StatusCardData>
                                                </StatusCard>
                                            </StatusCol>
                                        )
                                    })}
                                </ScrollableContainer>
                            </RobotStatusBody>
                        </RobotStatusContainer>
                    )}
                </>
            ) : (
                <RobotStatusBody>
                    <ScrollableContainer>
                        <StatusCard>
                            <StatusCardData isTitle>No Robots Added</StatusCardData>
                        </StatusCard>
                    </ScrollableContainer>
                </RobotStatusBody>
            )}
        </DashboardContainer>
    </StatusPageMainContainer>
  );
};

export default RobotStatusDashboard;
